import type { RecordingUploadTrack } from '@/shared/api/recording/types';

import { getSupportedRecorderMimeType, stopMediaRecorder, stopMediaStream } from '@/entities/recording';

const RECORDER_TIMESLICE_MS = 5_000;
const SCREEN_BITS_PER_SECOND = 6_000_000;
const CAMERA_BITS_PER_SECOND = 2_000_000;

/** Everything a recording captures; each stream is null until it is acquired or when it is not used. */
export type RecordingMedia = {
  audioContext: AudioContext | null;
  camera: MediaStream | null;
  display: MediaStream | null;
  microphone: MediaStream | null;
  /** The screen video with the screen and microphone audio mixed in: what the main track records. */
  mixed: MediaStream | null;
};

export type RecordingRecorders = {
  camera: MediaRecorder | null;
  main: MediaRecorder | null;
};

export const createEmptyRecordingMedia = (): RecordingMedia => ({
  audioContext: null,
  camera: null,
  display: null,
  microphone: null,
  mixed: null,
});

export const releaseRecordingMedia = ({ audioContext, camera, display, microphone, mixed }: Partial<RecordingMedia>) => {
  [display, camera, microphone, mixed].forEach((stream) => stopMediaStream(stream ?? null));
  if (audioContext) void audioContext.close();
};

export const getCameraConstraints = (cameraId: string): MediaStreamConstraints => ({
  audio: false,
  video: cameraId ? { deviceId: { exact: cameraId } } : true,
});

const getMicrophoneConstraints = (microphoneId: string): MediaStreamConstraints => ({
  audio: microphoneId ? { deviceId: { exact: microphoneId } } : true,
  video: false,
});

export const requestScreenShare = () =>
  navigator.mediaDevices.getDisplayMedia({
    audio: true,
    // The recorder works inside the app, so the current Rask tab must be pickable too.
    selfBrowserSurface: 'include',
    surfaceSwitching: 'include',
    video: { frameRate: { ideal: 30, max: 60 } },
  } as DisplayMediaStreamOptions);

/** Resolves to null for a device that is turned off or unavailable: the recording goes on without it. */
const requestOptionalDevice = (isEnabled: boolean, constraints: MediaStreamConstraints) =>
  isEnabled ? navigator.mediaDevices.getUserMedia(constraints).catch(() => null) : Promise.resolve(null);

type DeviceSelection = {
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  selectedCameraId: string;
  selectedMicrophoneId: string;
};

export const requestCameraAndMicrophone = ({
  cameraEnabled,
  microphoneEnabled,
  selectedCameraId,
  selectedMicrophoneId,
}: DeviceSelection) =>
  Promise.all([
    requestOptionalDevice(cameraEnabled, getCameraConstraints(selectedCameraId)),
    requestOptionalDevice(microphoneEnabled, getMicrophoneConstraints(selectedMicrophoneId)),
  ]);

const createTrackRecorder = (stream: MediaStream, options: MediaRecorderOptions, onChunk: (blob: Blob) => void) => {
  const recorder = new MediaRecorder(stream, options);
  recorder.addEventListener('dataavailable', (event) => onChunk(event.data));
  return recorder;
};

/** Records the mixed screen stream on the 'recording' track and, when there is one, the camera on its own track. */
export const createRecorders = (
  { camera, mixed }: { camera: MediaStream | null; mixed: MediaStream },
  onChunk: (track: RecordingUploadTrack, blob: Blob) => void,
): RecordingRecorders => {
  const main = createTrackRecorder(
    mixed,
    {
      mimeType: getSupportedRecorderMimeType(mixed.getAudioTracks().length > 0),
      videoBitsPerSecond: SCREEN_BITS_PER_SECOND,
    },
    (blob) => onChunk('recording', blob),
  );
  const cameraRecorder =
    camera &&
    createTrackRecorder(
      camera,
      { mimeType: getSupportedRecorderMimeType(false), videoBitsPerSecond: CAMERA_BITS_PER_SECOND },
      (blob) => onChunk('camera', blob),
    );

  return { camera: cameraRecorder, main };
};

export const startRecorders = ({ camera, main }: RecordingRecorders) => {
  main?.start(RECORDER_TIMESLICE_MS);
  camera?.start(RECORDER_TIMESLICE_MS);
};

export const pauseRecorders = ({ camera, main }: RecordingRecorders) => {
  main?.pause();
  camera?.pause();
};

export const resumeRecorders = ({ camera, main }: RecordingRecorders) => {
  main?.resume();
  camera?.resume();
};

/** Resolves once both recorders have stopped (and emitted their last chunk) or given up waiting. */
export const stopRecorders = ({ camera, main }: RecordingRecorders) =>
  Promise.all([stopMediaRecorder(main), stopMediaRecorder(camera)]);
